import { gql, useMutation } from "@apollo/client";
import { useCallback } from "react";

import Button from "ds/components/Button";
import FlashContext from "components/FlashMessages/FlashContext";
import useTypedContext from "hooks/useTypedContext";
import { trackSegmentEvent } from "shared/Analytics";
import { BillingTier } from "types/generated";
import { GET_TIER } from "views/Account/SubscriptionWrapper";

import { GET_ACCOUNT_DATA } from "../../../../gql";
import { GET_USAGE } from "../gql";

/** @public (Knip ignored - used in the file and exported for testing) */
export const UPDATE_SUBSCRIPTION = gql`
  mutation UpdateSubscription($tier: BillingTier!) {
    billingSubscriptionUpdateTier(tier: $tier) {
      isActive
      selfServePortalUrl
      billingCycleStart
    }
  }
`;

type BillingUpgradeButtonProps = {
  tier: BillingTier;
  onModalClose: () => void;
};

const BillingUpgradeButton = ({ tier, onModalClose }: BillingUpgradeButtonProps) => {
  const { onError, reportSuccess } = useTypedContext(FlashContext);
  const [updateSubscription, { loading }] = useMutation(UPDATE_SUBSCRIPTION, {
    refetchQueries: [{ query: GET_USAGE }, { query: GET_ACCOUNT_DATA }, { query: GET_TIER }],
  });

  const buttonText =
    tier === BillingTier.Enterprise ? "Downgrade to Cloud" : "Upgrade to Enterprise";

  const newTier = useCallback(() => {
    return tier === BillingTier.Enterprise ? BillingTier.V3Cloud : BillingTier.Enterprise;
  }, [tier]);

  const handleUpgrade = useCallback(() => {
    updateSubscription({ variables: { tier: newTier() } })
      .then(() => {
        reportSuccess({ message: "Subscription was updated" });
        trackSegmentEvent("Upgrade button clicked");
        onModalClose();
      })
      .catch(onError);
  }, [onError, onModalClose, newTier, reportSuccess, updateSubscription]);

  return (
    <Button type="button" onPress={handleUpgrade} variant="primary" loading={loading}>
      {buttonText}
    </Button>
  );
};

export default BillingUpgradeButton;
